import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { Badge } from '../../components/ui/Badge.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { financeApi } from '../../services/financeApi.js';
import { formatCurrency } from '../../utils/formatters.js';

export function BudgetCheckModal({ open, onClose, defaultYear }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({ accountId: '', year: defaultYear, month: 1, amount: '' });
  const [error, setError] = useState(null);

  const accounts = useQuery({
    queryKey: ['finance', 'accounts'],
    queryFn: () => financeApi.listAccounts().then((r) => r.items),
    enabled: open,
  });
  const expenseAccounts = (accounts.data || []).filter((a) => a.type === 'EXPENSE');

  useEffect(() => { if (open) { setForm({ accountId: '', year: defaultYear, month: 1, amount: '' }); setError(null); } }, [open, defaultYear]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const check = useMutation({
    mutationFn: () => financeApi.budgetCheck({ accountId: form.accountId, year: Number(form.year), month: Number(form.month), amount: Number(form.amount) }).then((r) => r.items),
    onError: (e) => setError(e?.message || t('common.loadError')),
    onSuccess: () => setError(null),
  });
  const result = check.data;

  function submit(e) {
    e.preventDefault();
    setError(null);
    if (!form.accountId || form.amount === '' || Number(form.amount) < 0) { setError(t('bud.error.checkRequired')); return; }
    check.mutate();
  }

  return (
    <Modal open={open} onClose={onClose} title={t('bud.check.title')}
      footer={<><Button variant="ghost" onClick={onClose}>{t('common.close')}</Button>
        <Button variant="primary" onClick={submit} disabled={check.isPending}>{t('bud.action.check')}</Button></>}>
      <form onSubmit={submit} className="form-stack">
        <label className="field"><span className="field-label">{t('bud.col.account')}</span>
          <select className="input" value={form.accountId} onChange={set('accountId')} required>
            <option value="">-</option>
            {expenseAccounts.map((a) => <option key={a.id} value={a.id}>{a.code} · {a.name}</option>)}
          </select>
        </label>
        <div className="field-row">
          <label className="field" style={{ maxWidth: 120 }}><span className="field-label">{t('bud.field.year')}</span>
            <input className="input num" type="number" value={form.year} onChange={set('year')} />
          </label>
          <label className="field" style={{ maxWidth: 120 }}><span className="field-label">{t('bud.field.month')}</span>
            <select className="input" value={form.month} onChange={set('month')}>
              {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </label>
          <label className="field"><span className="field-label">{t('bud.check.amount')}</span>
            <input className="input num" type="number" min="0" step="0.01" value={form.amount} onChange={set('amount')} required />
          </label>
        </div>
        {error && <div className="alert danger">{error}</div>}
        {result && (
          <div className="card" style={{ padding: 'var(--space-3)' }}>
            {result.controlled ? (
              <div className="form-stack" style={{ gap: 'var(--space-1)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}><span className="muted">{t('bud.col.budgeted')}</span><span className="mono num">{formatCurrency(result.budgeted)}</span></div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}><span className="muted">{t('bud.col.actual')}</span><span className="mono num">{formatCurrency(result.actual)}</span></div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}><span className="muted">{t('bud.col.available')}</span><span className="mono num">{formatCurrency(result.available)}</span></div>
                <div style={{ marginTop: 'var(--space-2)' }}>
                  {result.wouldExceed
                    ? <Badge variant="danger">{t('bud.check.exceed')}</Badge>
                    : <Badge variant="success">{t('bud.check.ok')}</Badge>}
                </div>
              </div>
            ) : <div className="muted">{t('bud.check.uncontrolled')}</div>}
          </div>
        )}
      </form>
    </Modal>
  );
}
